/* eslint-disable camelcase */
import { makeAutoObservable, runInAction } from 'mobx';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import { makePersistable, clearPersistedStore } from 'mobx-persist-store';

import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';

export const DEFAULT_LOCALE = 'en';

export const localeMessages = Object.freeze({
  en: Object.freeze({
    loadMessages: () => import('../../product-framework/locales/en.json'),
    muiTableLocalization: MRT_Localization_EN,
  }),
  es: Object.freeze({
    loadMessages: () => import('../../product-framework/locales/es.json'),
    muiTableLocalization: MRT_Localization_ES,
  }),
  fr: Object.freeze({
    loadMessages: () => import('../../product-framework/locales/fr.json'),
    muiTableLocalization: MRT_Localization_FR,
  }),
});

function getBrowserLocale() {
  return navigator?.language?.split('-')[0] || null;
}

export async function getLocaleMessages(locale) {
  const descriptor = localeMessages[locale] || localeMessages[DEFAULT_LOCALE];
  return descriptor.loadMessages();
}

// Initialize with Browser Language
// If Browser language is not supported then use default language
// You can set proper supported and user language after login
// Language can also be changed from language selector

class LocaleStore {
  browserLocale = getBrowserLocale() || DEFAULT_LOCALE;

  staticLocalesSupported = Object.freeze(Object.keys(localeMessages));

  locale = this.staticLocalesSupported.includes(this.browserLocale)
    ? this.browserLocale
    : DEFAULT_LOCALE;

  localesSupported = this.staticLocalesSupported;

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this);

    makePersistable(this, {
      name: 'LocaleStore',
      properties: ['locale', 'localesSupported'],
      storage: window.localStorage,
    });
  }

  set(value = DEFAULT_LOCALE) {
    runInAction(() => {
      if (value) {
        this.locale = value;
      }
    });
  }

  setSupportedLocales(value = []) {
    runInAction(() => {
      if (Array.isArray(value)) {
        this.localesSupported = value;
      }
    });
  }

  get() {
    return this.locale;
  }

  getSupportedLocales() {
    return this.localesSupported;
  }

  getLocaleForMuiTable() {
    return (localeMessages[this.locale] || localeMessages[DEFAULT_LOCALE])
      .muiTableLocalization;
  }

  async clearStore() {
    const DONT_CLEAR = true;
    if (!DONT_CLEAR) {
      await clearPersistedStore(this);
    }
  }
}

export default LocaleStore;
